import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import MapGL, { Source, Layer, Marker } from "react-map-gl";
import { Button, Empty, Form, List, Spin, Switch, Table, Tooltip } from "antd";
import { LuPrinter } from "react-icons/lu";
import polylabel from "polylabel";
import { geoArea } from "d3-geo";
import bbox from "@turf/bbox";
import center from "@turf/center";
import axios from "axios";
import secureLocalStorage from "react-secure-storage";
import Header from "../../Components/Header";
import Footer from "../../Components/Footer";
import hostURL from "../../Common/constants";
import { closeWizard } from "../../Redux/wizardSlice";
import {
  stateFilter,
  safeLabels,
  priorityLabels,
  crepColorPool,
  crepColorMap as CREP_COLOR_EXPORT,
  crepCodes,
} from "../FieldReport/utils/helperFunctions";
import "./style.css";
import useTutorialSteps from "../Tutorial/useTutorialSteps";
import {
  TbHexagonLetterP,
  TbHexagonLetterR,
  TbHexagonLetterS,
  TbHexagonLetterW,
} from "react-icons/tb";
import { LuMap } from "react-icons/lu";
import PrintHelp from "./Components/PrintHelp";

const FieldPrint = () => {
  // ***************** START NEW FILTERING STATE VARIABLES **************************

  // Normalize CREP colors to a stable Map, even if something passed it as an object.
  const crepColorsRef = useRef(null);
  if (!crepColorsRef.current) {
    const maybe = CREP_COLOR_EXPORT;
    crepColorsRef.current =
      maybe instanceof Map ? maybe : new Map(Object.entries(maybe || {}));
  }
  const crepColors = crepColorsRef.current;

  const hasCrepColor = (k) =>
    crepColors instanceof Map
      ? crepColors.has(k)
      : Object.prototype.hasOwnProperty.call(crepColors, k);

  const getCrepColor = (k) =>
    crepColors instanceof Map ? crepColors.get(k) : crepColors?.[k];

  const setCrepColor = (k, v) => {
    if (crepColors instanceof Map) crepColors.set(k, v);
    else crepColors[k] = v;
    return v;
  };

  // ***************** END NEW FILTERING STATE VARIABLES **************************

  const [loading, setLoading] = useState(false);
  const [soilData, setSoilData] = useState(null);
  const [safeData, setSafeData] = useState(null);
  const [floodplainData, setFloodplainData] = useState(null);
  const [waterData, setWaterData] = useState(null);
  const [hydroData, setHydroData] = useState(null);
  const [majorSoilTypes, setMajorSoilTypes] = useState([]);
  const [majorSoilArea, setMajorSoilArea] = useState([]);
  const [fieldBasics, setFieldBasics] = useState(null);
  const [fieldFeatureData, setFieldFeatureData] = useState(null);
  const [fieldAddress, setFieldAddress] = useState("Not Specified");
  const [fieldSize, setFieldSize] = useState(null);
  const [fieldRentalRate, setFieldRentalRate] = useState(null);
  const [soilTableData, setSoilTableData] = useState(null);
  const [grasslandData, setGrasslandData] = useState(null);
  const [pastureData, setPastureData] = useState(null);
  const [baseLayer, setBaseLayer] = useState(
    "mapbox://styles/mapbox/satellite-streets-v12"
  );
  const [layerSelectOpen, setLayerSelectOpen] = useState(false);
  const [soilLayerOn, setSoilLayerOn] = useState(true);
  const [safeLayerOn, setSafeLayerOn] = useState(false);
  const [floodplainLayerOn, setFloodplainLayerOn] = useState(false);
  const [waterLayerOn, setWaterLayerOn] = useState(false);
  const [filter, setFilter] = useState([]);
  const [highlightFeatureId, setHighlightFeatureId] = useState(null);

  const [serviceCenterList, setServiceCenterList] = useState([]);
  const [userFavoritePractices, setUserFavoritePractices] = useState([]);
  const [userFavoritesLoaded, setUserFavoritesLoaded] = useState(false);

  const INCENTIVE_LABELS = {
    S: { label: "Signup incentive", note: "(1 time payment increase)" },
    R: { label: "Rental Rate incentive", note: "(Annual payment increase)" },
    P: { label: "Practice incentive", note: "(1 time payment increase)" },
    W: { label: "Water Quality Bonus", note: "(Through CLEAR30)" },
  };

  const ICONS = {
    S: <TbHexagonLetterS size="2em" color="maroon" />,
    R: <TbHexagonLetterR size="2em" color="maroon" />,
    P: <TbHexagonLetterP size="2em" color="maroon" />,
    W: <TbHexagonLetterW size="2em" color="maroon" />,
  };

  const { handleNextStep } = useTutorialSteps();
  const { currentStep, isTutorialActive } = useSelector(
    (state) => state.tutorial
  );

  const navigate = useNavigate();
  const location = useLocation();
  const mapRef = useRef();
  const dispatch = useDispatch();
  const [mapLoaded, setMapLoaded] = useState(false);

  const handlePrint = () => window.print();

  // IMPORTANT: reset each render so we can assign colors deterministically per render pass
  let nextColorIndex = 0;

  const scoredAndSortedPractices = (() => {
    try {
      const raw = localStorage.getItem("fieldreport:visiblePractices");
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  })();

  const [form] = Form.useForm();
  const authToken = secureLocalStorage.getItem("token");
  const fieldDetails = secureLocalStorage.getItem("field-details");

  const {
    landCoverSelections,
    prioritySelections,
    selectedFips,
    selectedState,
    selectedCounty,
  } = useSelector((state) => state.filter);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    dispatch(closeWizard());
  }, [dispatch]);

  useEffect(() => {
    const polygonFips = fieldBasics?.fips;
    if (!polygonFips) return;

    if (selectedFips && String(polygonFips) !== String(selectedFips)) {
      console.warn("FIPS mismatch - redirecting to draw polygon step");
      navigate("/Map", { replace: true });
    }
  }, [fieldBasics, selectedFips, navigate]);

  const getFieldAddress = async () => {
    try {
      const fieldGeometry = JSON.parse(
        fieldDetails.countyRate.rows[0].geometry
      );
      const centerPoint = center(fieldGeometry);
      const [lng, lat] = centerPoint.geometry.coordinates;
      setLoading(true);

      const response = await axios.get(
        "https://api.mapbox.com/search/geocode/v6/reverse?longitude=" +
          lng +
          "&latitude=" +
          lat +
          "&access_token=pk.eyJ1IjoiY2h1Y2swNTIwIiwiYSI6ImNrMDk2NDFhNTA0bW0zbHVuZTk3dHQ1cGUifQ.dkjP73KdE6JMTiLcUoHvUA"
      );

      if (response?.data?.features?.[0]?.properties?.full_address) {
        setFieldAddress(response.data.features[0].properties.full_address);
      }
    } catch (e) {
      console.error("Error getting field address:", e);
    } finally {
      setTimeout(() => setLoading(false), 1000);
    }
  };

  const getFieldRentalRate = () => {
    const fieldRate = fieldDetails.fieldRate;
    const fieldSpecifics = fieldRate.rows;

    const soilTypes = [
      ...new Set(fieldSpecifics.map((field) => field.musym)),
    ].filter((soilType) => soilType !== "W");

    const SoilTypeData = soilTypes.map((soilType) => {
      const fieldsBySoilType = fieldSpecifics?.filter(
        (field) => field.musym === soilType
      );
      const srr = fieldsBySoilType[0].srr;
      const areas = fieldsBySoilType.map((field) => field.area);
      const totalArea = areas.reduce((total, area) => total + area, 0);
      return { type: soilType, srr, totalArea };
    });

    const getTopThree = (arr) => {
      arr.sort((a, b) => (a < b ? 1 : a > b ? -1 : 0));
      return arr.slice(0, 3);
    };

    const totalAreas = SoilTypeData.map((soilType) => soilType.totalArea);
    const topThreeTotalAreas = getTopThree(totalAreas);
    const topThreeSoilTypes = SoilTypeData?.filter((soilType) =>
      topThreeTotalAreas.includes(soilType.totalArea)
    );
    setMajorSoilTypes(topThreeSoilTypes);
    setMajorSoilArea(topThreeTotalAreas.reduce((a, b) => a + b, 0) * 0.000247105);
  };

  const EMPTY_FC = useMemo(
    () => ({ type: "FeatureCollection", features: [] }),
    []
  );

  const interactiveLayerIds = useMemo(() => ["soil-layer"], []);

  const getUserFavorites = () => {
    fetch(
      `${hostURL}api/userfavourite/${secureLocalStorage.getItem(
        "userRef"
      )}/${selectedState}/${selectedCounty}`,
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: authToken,
        },
      }
    )
      .then((response) => response.json())
      .then((json) => {
        const rawFavorites = Array.isArray(json)
          ? json.map((item) => item.favTitle)
          : json.data && Array.isArray(json.data)
          ? json.data.map((item) => item.favTitle)
          : [];

        const respData = Array.from(new Set(rawFavorites));
        setUserFavoritePractices(respData);
        setUserFavoritesLoaded(true);
      })
      .catch((error) => {
        console.error("Error fetching favorites:", error);
        setUserFavoritesLoaded(true);
      });
  };

  function getTopThreeSoilsTable(fieldRateRows) {
    if (!Array.isArray(fieldRateRows)) return [];

    return Object.values(
      fieldRateRows.reduce((acc, r) => {
        const k = r.musym;
        acc[k] ??= { key: k, soil_type: k, srr: r.srr, totalArea: 0 };
        acc[k].totalArea += r.area;
        return acc;
      }, {})
    )
      .sort((a, b) => b.totalArea - a.totalArea)
      .slice(0, 3)
      .map((s) => {
        const rawSrr = s.srr;

        const isMissingSrr =
          rawSrr === null ||
          rawSrr === undefined ||
          rawSrr === "null" ||
          rawSrr === "";

        return {
          key: s.key,
          soil_type: s.soil_type,
          raw_srr: rawSrr,
          rental_rate: isMissingSrr ? "N/A" : `$${rawSrr}/acre`,
          acreage: Math.round(s.totalArea * 0.000247105 * 100) / 100,
          isMissingSrr,
        };
      });
  }

  const topThreeSoilsTable = useMemo(() => {
    return getTopThreeSoilsTable(fieldDetails?.fieldRate?.rows);
  }, [fieldDetails]);

  const hasMissingTopThreeSrr = useMemo(() => {
    return topThreeSoilsTable?.some(
      (row) =>
        row?.isMissingSrr ||
        row?.raw_srr === null ||
        row?.raw_srr === undefined ||
        row?.raw_srr === "null"
    );
  }, [topThreeSoilsTable]);

  const getServiceCenter = async (queryState, queryCounty) => {
    try {
      const response = await axios.post(hostURL + "api/field/service-center", {
        state: queryState,
        county: queryCounty,
      });

      if (response?.data?.serviceCenter?.rows) {
        const fsaServiceCenters = response.data.serviceCenter.rows.filter(
          (item) => item.service_center.includes("Farm Service Agency")
        );
        setServiceCenterList(fsaServiceCenters);
      }
    } catch (e) {
      console.error("Error fetching service center:", e);
    }
  };

  const handleLayerSelect = () => {
    if (isTutorialActive && currentStep === 60) handleNextStep();
    setLayerSelectOpen(!layerSelectOpen);
  };

  const getMarkerLocation = (geometry) => {
    let output = [];
    if (geometry.type === "Polygon") {
      output = polylabel(geometry.coordinates, 0.000001);
    } else {
      let maxArea = 0;
      let maxPolygon = [];
      for (let i = 0, l = geometry.coordinates.length; i < l; i++) {
        const p = geometry.coordinates[i];
        const area = geoArea({ type: "Polygon", coordinates: p });
        if (area > maxArea) {
          maxPolygon = p;
          maxArea = area;
        }
      }
      output = polylabel(maxPolygon, 0.000001);
    }
    return output;
  };

  const onMouseMove = (e) => {
    const feature = e?.features?.[0];
    if (feature) {
      setFilter(["in", "gid", feature.id]);
      setHighlightFeatureId(feature.id);
    } else {
      setFilter(["in", "gid", ""]);
      setHighlightFeatureId(null);
    }
  };

  useEffect(() => {
    if (!fieldDetails) return;

    const basics = fieldDetails.countyRate.rows[0];
    setFieldBasics(basics);
    getFieldRentalRate();
    setFieldSize(basics.area * 0.000247105);
    setFieldFeatureData({
      type: "Feature",
      geometry: JSON.parse(basics.geometry),
    });

    getServiceCenter(basics.state, basics.county);
    getFieldAddress();

    const soilTable = fieldDetails.fieldRate.rows.map((soil) => ({
      key: soil.gid,
      soil_type: soil.musym,
      rental_rate: "$" + soil.srr + "/acre",
      acreage: Math.round(soil.area * 0.000247105 * 100) / 100,
    }));
    setSoilTableData(soilTable.sort((a, b) => b.acreage - a.acreage));

    const soilFeatures = fieldDetails.fieldRate.rows.map((soil) => {
      const soilGeometry = JSON.parse(soil.geometry);
      return {
        type: "Feature",
        geometry: soilGeometry,
        id: soil.gid,
        properties: { gid: soil.gid, musym: soil.musym },
      };
    });
    setSoilData({ type: "FeatureCollection", features: soilFeatures });

    if (fieldDetails.safeArea.rows.length > 0) {
      const safeFeatures = fieldDetails.safeArea.rows.map((safe) => {
        const safeGeometry = JSON.parse(safe.geometry);
        return { type: "Feature", geometry: safeGeometry };
      });
      setSafeData({ type: "FeatureCollection", features: safeFeatures });
    }

    if (fieldDetails.floodPlain.rows.length > 0) {
      const floodplainFeatures = fieldDetails.floodPlain.rows.map(
        (floodplain) => {
          const floodplainGeometry = JSON.parse(floodplain.geometry);
          return { type: "Feature", geometry: floodplainGeometry };
        }
      );
      setFloodplainData({
        type: "FeatureCollection",
        features: floodplainFeatures,
      });
    }

    if (fieldDetails.waterBuffer.rows.length > 0) {
      const waterBufferFeatures = fieldDetails.waterBuffer.rows.map(
        (waterBuffer) => {
          const waterBufferGeometry = JSON.parse(waterBuffer.geometry);
          return { type: "Feature", geometry: waterBufferGeometry };
        }
      );
      setWaterData({
        type: "FeatureCollection",
        features: waterBufferFeatures,
      });
    }

    if (fieldDetails.hydricSoil.rows.length > 0) {
      const hydricSoilFeatures = fieldDetails.hydricSoil.rows.map(
        (hydricSoil) => {
          const hydricSoilGeometry = JSON.parse(hydricSoil.geometry);
          return {
            type: "Feature",
            geometry: hydricSoilGeometry,
            id: hydricSoil.gid,
            properties: {
              gridcode: hydricSoil.gridcode,
              area: hydricSoil.area,
            },
          };
        }
      );
      setHydroData({ type: "FeatureCollection", features: hydricSoilFeatures });
    }

    if (fieldDetails.grasslandRentalRate.rows.length > 0) {
      const grasslandRecord = fieldDetails.grasslandRentalRate.rows[0];
      setGrasslandData({
        fips: grasslandRecord.fips,
        grr: grasslandRecord.grr,
      });
    }

    if (fieldDetails.pastureRentalRate.rows.length > 0) {
      const pastureRecord = fieldDetails.pastureRentalRate.rows[0];
      setPastureData({ fips: pastureRecord.fips, mplrr: pastureRecord.mplrr });
    }

    secureLocalStorage.setItem("field-details", fieldDetails);
  }, [fieldDetails]);

  useEffect(() => {
    if (!majorSoilTypes?.length) return;

    const rateBySoilTypes = majorSoilTypes.map((soilType) => {
      const areaInAcre = soilType.totalArea * 0.0002471;
      const rateBySoilType = soilType.srr * areaInAcre;
      return rateBySoilType;
    });

    const totalRentalRate = Math.round(
      rateBySoilTypes.reduce((t, r) => t + r, 0)
    );
    setFieldRentalRate(totalRentalRate);
  }, [majorSoilTypes]);

  useEffect(() => {
    if (!mapLoaded || !mapRef.current || !fieldFeatureData) return;

    const [minLng, minLat, maxLng, maxLat] = bbox(fieldFeatureData);
    mapRef.current.fitBounds(
      [
        [minLng, minLat],
        [maxLng, maxLat],
      ],
      { padding: 40, duration: 1000 }
    );
  }, [fieldFeatureData, mapLoaded]);

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const userId = secureLocalStorage.getItem("userRef");
    if (!userId) {
      setUserFavoritesLoaded(true);
      return;
    }

    if (selectedState && selectedCounty && !userFavoritesLoaded) {
      getUserFavorites();
    }
  }, [selectedState, selectedCounty, userFavoritesLoaded]);

  useEffect(() => {
    return () => {
      const map = mapRef.current?.getMap?.();
      if (map?.setTerrain) map.setTerrain(null);
    };
  }, []);

  if (!fieldDetails) {
    navigate("/");
    return null;
  }

  const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const formatCrepValue = (obj) => {
    if (!obj) return null;

    const percent = toNum(obj.percentage);
    if (percent !== null && percent > 0)
      return { type: "percent", value: percent };

    const perAcre = toNum(obj.perAcre);
    if (perAcre !== null && perAcre > 0)
      return { type: "perAcre", value: perAcre };

    return null;
  };

  const getEffectiveIncentives = (base, selectedCrep) => {
    const out = { ...base };
    if (!selectedCrep) return out;

    const sip = formatCrepValue(selectedCrep.sip);
    if (sip) out.S = sip;
    else if (selectedCrep.sip && !sip) out.S = null;

    const pip = formatCrepValue(selectedCrep.pip);
    if (pip) out.P = pip;
    else if (selectedCrep.pip && !pip) out.P = null;

    const crepR = toNum(selectedCrep.newenrollmentsrr) || 0;
    out.R = crepR > 0 ? { type: "percent", value: crepR } : null;

    out.W = null;
    return out;
  };

  const renderIncentives = (effective) => {
    const keys = ["S", "R", "P", "W"];

    const rows = keys
      .map((key) => {
        const info = INCENTIVE_LABELS[key];
        if (!info) return null;

        const val = effective?.[key];
        if (!val) return null;

        let display;
        if (typeof val === "number") {
          display = <b>{val}%</b>;
        } else if (val.type === "percent") {
          display = <b>{val.value}%</b>;
        } else if (val.type === "perAcre") {
          display = <b>${val.value.toLocaleString()}/acre</b>;
        } else {
          return null;
        }

        return (
          <div
            key={key}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "6px",
              marginBottom: "6px",
            }}
          >
            <span style={{ flexShrink: 0 }}>{ICONS[key]}</span>
            <span>
              {display} {info.label} <i>{info.note}</i>
            </span>
          </div>
        );
      })
      .filter(Boolean);

    // IMPORTANT: return null when there are no incentives
    if (!rows.length) return null;

    return rows;
  };

  return (
    <>
      <Header />
      <Spin spinning={loading}>
        <div className="report print-view">
          <div className="print-toolbar">
            <Button
              type="primary"
              className="print-toolbar-btn"
              icon={<LuPrinter />}
              onClick={handlePrint}
            >
              Print
            </Button>
          </div>

          <div className="report-head-container">
            <div
              className="report-basic-cont"
              style={{ display: "flex", flexDirection: "column" }}
            >
              <h2 className={"report-header"}>
                CRP Field Eligibility Report:
                <span
                  style={{ fontWeight: "normal" }}
                >{` ${fieldBasics?.county}, ${fieldBasics?.state}`}</span>
              </h2>

              <div className="basic-info-container">
                <div className="report-basic-information">
                  <p />
                  <p>
                    <b>FIPS Code: </b>
                    {fieldBasics?.fips} | <b>Field Size: ≈ </b>
                    {Math.floor(fieldSize)} acres
                  </p>

                  <p>
                    <b>SAFE Zone(s): </b>
                    {fieldDetails.safeArea.rows.length === 0
                      ? "No"
                      : fieldDetails.safeArea.rows
                          .map((item) => safeLabels[item.code])
                          .join(", ")}{" "}
                    | <b>Floodplain: </b>
                    {fieldDetails.floodPlain.rows.length ? "Yes" : "No"} |{" "}
                    <b>Selected Priorities: </b>
                    {prioritySelections.length
                      ? prioritySelections
                          .map((priority, index) =>
                            index === 0
                              ? priorityLabels[priority]
                              : ", " + priorityLabels[priority]
                          )
                          .join("")
                      : "No priority selected"}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <hr className="report-hr" />

          <div className="report-map-container">
            <h3 className={"report-section-header"}>Field Boundary:</h3>

            <div className="report-map-inner-container">
              <MapGL
                id="fieldReportMap"
                ref={mapRef}
                mapboxAccessToken="pk.eyJ1IjoiY2h1Y2swNTIwIiwiYSI6ImNrMDk2NDFhNTA0bW0zbHVuZTk3dHQ1cGUifQ.dkjP73KdE6JMTiLcUoHvUA"
                preserveDrawingBuffer={true}
                terrain={null}
                initialViewState={{ longitude: -91, latitude: 36, zoom: 4 }}
                mapStyle={baseLayer}
                interactiveLayerIds={interactiveLayerIds}
                onMouseMove={onMouseMove}
                onLoad={() => setMapLoaded(true)}
              >
                <Tooltip placement="bottom" title="Layer Tool">
                  <Button
                    className="report-layer-select"
                    onClick={handleLayerSelect}
                  >
                    <LuMap strokeWidth={3} />
                  </Button>
                </Tooltip>

                {layerSelectOpen && (
                  <div className="report-layer-options">
                    <div className="base-layers">
                      <p>
                        <b>Base Layers</b>
                      </p>

                      <p>
                        <img
                          alt="thumbnail for light view of map"
                          className="layer-thumbnail"
                          src="https://api.mapbox.com/styles/v1/mapbox/light-v11/static/-88.81,33.45,7.0,0,0/50x50?access_token=pk.eyJ1IjoiY2h1Y2swNTIwIiwiYSI6ImNrMDk2NDFhNTA0bW0zbHVuZTk3dHQ1cGUifQ.dkjP73KdE6JMTiLcUoHvUA"
                        />

                        <Switch
                          className="report-layer-switch"
                          checked={
                            baseLayer ===
                            "mapbox://styles/mapbox/satellite-streets-v12"
                          }
                          checkedChildren="Satellite"
                          unCheckedChildren="light"
                          defaultChecked
                          onChange={(checked) => {
                            const map = mapRef.current?.getMap?.();
                            try {
                              if (map?.getTerrain?.() && map?.setTerrain)
                                map.setTerrain(null);
                            } catch {}
                            setBaseLayer(
                              checked
                                ? "mapbox://styles/mapbox/satellite-streets-v12"
                                : "mapbox://styles/mapbox/light-v11"
                            );
                          }}
                        />

                        <img
                          alt="thumbnail for satellite view of map"
                          className="layer-thumbnail"
                          src="https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v12/static/-88.81,33.45,7.0,0,0/50x50?access_token=pk.eyJ1IjoiY2h1Y2swNTIwIiwiYSI6ImNrMDk2NDFhNTA0bW0zbHVuZTk3dHQ1cGUifQ.dkjP73KdE6JMTiLcUoHvUA"
                        />
                      </p>
                    </div>

                    <hr />

                    <div className="supporting-layers">
                      <p>
                        <b>Supporting Layers</b>
                      </p>

                      <p>
                        <input
                          type="checkbox"
                          checked={soilLayerOn}
                          onChange={(e) =>
                            setSoilLayerOn(!!e.currentTarget.checked)
                          }
                        />
                        Soil Types
                        <div className="report-legend-soil" />
                      </p>

                      <p>
                        <input
                          type="checkbox"
                          checked={safeLayerOn}
                          onChange={(e) =>
                            setSafeLayerOn(!!e.currentTarget.checked)
                          }
                        />
                        SAFE Boundaries
                        <div className="report-legend-safe" />
                      </p>

                      <p>
                        <input
                          type="checkbox"
                          checked={floodplainLayerOn}
                          onChange={(e) =>
                            setFloodplainLayerOn(!!e.currentTarget.checked)
                          }
                        />
                        Floodplains
                        <div className="report-legend-floodplain" />
                      </p>

                      <p>
                        <input
                          type="checkbox"
                          checked={waterLayerOn}
                          onChange={(e) =>
                            setWaterLayerOn(!!e.currentTarget.checked)
                          }
                        />
                        Water Features
                        <div className="report-legend-water" />
                      </p>
                    </div>
                  </div>
                )}

                <Source
                  id="map-source"
                  type="vector"
                  url="mapbox://chuck0520.5pq9omti"
                  maxzoom={22}
                  minzoom={0}
                >
                  <Layer
                    id="county-boundary"
                    source-layer="c_08mr23-1pp4eg"
                    type="line"
                    paint={{
                      "line-color": "#800000",
                      "line-width": 1,
                      "line-opacity": 0.4,
                    }}
                    minzoom={0}
                    maxzoom={22}
                    filter={stateFilter}
                  />
                </Source>

                <Source
                  id="field-source"
                  type="geojson"
                  data={fieldFeatureData || EMPTY_FC}
                >
                  <Layer
                    id="field"
                    type="line"
                    paint={{ "line-color": "white", "line-width": 2 }}
                  />
                </Source>

                <Source
                  id="soil-source"
                  type="geojson"
                  data={soilData || EMPTY_FC}
                >
                  <Layer
                    id="soil-layer"
                    type="fill"
                    paint={{
                      "fill-outline-color": "white",
                      "fill-color": "transparent",
                    }}
                    layout={{ visibility: soilLayerOn ? "visible" : "none" }}
                  />
                  <Layer
                    id="soil-layer-highlight"
                    type="fill"
                    paint={{ "fill-color": "purple", "fill-opacity": 0.5 }}
                    filter={filter}
                    layout={{ visibility: soilLayerOn ? "visible" : "none" }}
                  />
                </Source>

                <Source
                  id="safe-source"
                  type="geojson"
                  data={safeData || EMPTY_FC}
                >
                  <Layer
                    id="safe"
                    type="fill"
                    paint={{ "fill-color": "orange", "fill-opacity": 0.5 }}
                    layout={{ visibility: safeLayerOn ? "visible" : "none" }}
                  />
                </Source>

                <Source
                  id="floodplain-source"
                  type="geojson"
                  data={floodplainData || EMPTY_FC}
                >
                  <Layer
                    id="floodplain"
                    type="fill"
                    paint={{ "fill-color": "blue", "fill-opacity": 0.5 }}
                    layout={{
                      visibility: floodplainLayerOn ? "visible" : "none",
                    }}
                  />
                </Source>

                <Source
                  id="water-source"
                  type="geojson"
                  data={waterData || EMPTY_FC}
                >
                  <Layer
                    id="water"
                    type="fill"
                    paint={{ "fill-color": "cyan", "fill-opacity": 0.5 }}
                    layout={{ visibility: waterLayerOn ? "visible" : "none" }}
                  />
                </Source>

                {fieldDetails.fieldRate.rows.map((soil) => {
                  const soilGeometry = JSON.parse(soil.geometry);
                  const [lng, lat] = getMarkerLocation(soilGeometry);

                  return soilLayerOn ? (
                    <Marker
                      key={soil.gid}
                      longitude={lng}
                      latitude={lat}
                      anchor="center"
                    >
                      <label style={{ color: "white" }}>{soil.musym}</label>
                    </Marker>
                  ) : null;
                })}
              </MapGL>
            </div>
          </div>

          <div>
            <p className="report-disclaimer">
              Rental rates vary depending on cover type and practice selection.
              Payment estimates from enrolling marginal pastureland in CP22,
              CP29, and CP30 will be reflected based on the county Marginal
              Pastureland Rental Rate (MPLRR). Payment estimates from enrolling
              in CP88 will be reflected based on the county Grassland Rental
              Rate (GRR).
            </p>
          </div>

          <div className="report-rental-table-container">
            <div>
              <h3 className={"report-section-header"}>
                Soil Rental Rate<sup>[1]</sup>:
              </h3>

              <p className="report-table-information">
                This is a breakdown table of the three predominant soil types
                and corresponding rental rate regarding your field.
              </p>

              <Table
                className="dropdown-report-table"
                pagination={false}
                size="small"
                columns={[
                  {
                    title: "Type",
                    dataIndex: "soil_type",
                    key: "soil_type",
                    align: "center",
                  },
                  {
                    title: "Rate",
                    dataIndex: "rental_rate",
                    key: "rental_rate",
                    align: "center",
                  },
                  {
                    title: "Acreage",
                    dataIndex: "acreage",
                    key: "acreage",
                    align: "center",
                  },
                ]}
                dataSource={topThreeSoilsTable}
              />
            </div>

            <div>
              <h3 className={"report-section-header"}>
                Marginal Pastureland Rental Rate<sup>[1]</sup>:
              </h3>

              <p className="report-table-information">
                This is the county rate for enrolling your field in a Marginal
                Pastureland CRP practice (CP29, CP30, and sometimes CP22).
              </p>

              <Table
                className={"report-table"}
                columns={[
                  {
                    title: "County",
                    dataIndex: "county",
                    key: "county",
                    align: "center",
                  },
                  {
                    title: "Rental Rate",
                    dataIndex: "rental_rate",
                    key: "rental_rate",
                    align: "center",
                  },
                  {
                    title: "Acreage",
                    dataIndex: "acreage",
                    key: "acreage",
                    align: "center",
                  },
                ]}
                dataSource={[
                  {
                    key: pastureData?.fips,
                    county: fieldBasics?.county,
                    rental_rate: "$" + pastureData?.mplrr + "/acre",
                    acreage: Math.round(fieldSize * 100) / 100,
                  },
                ]}
                pagination={false}
                size="large"
              />
            </div>

            <div>
              <h3 className={"report-section-header"}>
                Grassland Rental Rate<sup>[1]</sup>:
              </h3>

              <p className="report-table-information">
                This is the county rate for enrolling your field in a Grassland
                CRP practice (CP88).
              </p>

              <Table
                className={"report-table"}
                columns={[
                  {
                    title: "County",
                    dataIndex: "county",
                    key: "county",
                    align: "center",
                  },
                  {
                    title: "Rental Rate",
                    dataIndex: "rental_rate",
                    key: "rental_rate",
                    align: "center",
                  },
                  {
                    title: "Acreage",
                    dataIndex: "acreage",
                    key: "acreage",
                    align: "center",
                  },
                ]}
                dataSource={[
                  {
                    key: grasslandData?.fips,
                    county: fieldBasics?.county,
                    rental_rate: "$" + grasslandData?.grr + "/acre",
                    acreage: Math.round(fieldSize * 100) / 100,
                  },
                ]}
                pagination={false}
                size="large"
              />
            </div>
          </div>

          <div className="map-practice-container">
            <div className="report-practice-container">
              <h3 className={"report-section-header"}>
                Eligible CRP Practices:
              </h3>

              <p style={{ width: "90%", marginTop: "-5px" }}>
                Please click on a practice below to view practice specific field
                rental rates and use the <em>Details</em> link for more
                information on each practice.
              </p>

              <Spin spinning={loading}>
                {
                  <List
                    className="print report-practice-list"
                    grid={{ gutter: 16, column: 1 }}
                    locale={{
                      emptyText: (
                        <Empty
                          description={
                            "No eligible practices found for your selections."
                          }
                        />
                      ),
                    }}
                    dataSource={scoredAndSortedPractices}
                    renderItem={(practice, index) => {
                      const practiceTitle = practice?.title
                        ?.split("-")?.[0]
                        ?.trim();

                      const eligibleCREPs =
                        Array.isArray(practice?.crepInfo) &&
                        Array.isArray(fieldDetails?.crepArea?.rows)
                          ? practice.crepInfo.filter((crep) =>
                              fieldDetails.crepArea.rows
                                .map((item) => item.code)
                                .includes(crepCodes[crep.crepname])
                            )
                          : [];

                      eligibleCREPs.forEach((crep) => {
                        const title = crep.crepname;
                        if (!hasCrepColor(title)) {
                          setCrepColor(
                            title,
                            crepColorPool[nextColorIndex % crepColorPool.length]
                          );
                          nextColorIndex++;
                        }
                      });

                      const isPasturePractice = (pTitle) =>
                        (pTitle === "CP22" &&
                          landCoverSelections.length === 1 &&
                          landCoverSelections[0] === "pasture") ||
                        pTitle === "CP29" ||
                        pTitle === "CP30";

                      const isGrasslandPractice = (pTitle) => pTitle === "CP88";

                      const getPerAcreBaseRateInfo = (pTitle) => {
                        if (isPasturePractice(pTitle)) {
                          return {
                            label:
                              "Your marginal pastureland rental rate per acre",
                            perAcre: pastureData?.mplrr
                              ? Math.round(pastureData.mplrr)
                              : null,
                          };
                        }

                        if (isGrasslandPractice(pTitle)) {
                          return {
                            label: "Your grassland rental rate per acre",
                            perAcre: grasslandData?.grr
                              ? Math.round(grasslandData.grr)
                              : null,
                          };
                        }

                        const perAcre =
                          majorSoilArea && fieldRentalRate
                            ? Math.round(fieldRentalRate / majorSoilArea)
                            : null;

                        return {
                          label:
                            "Your total weighted average base rental rate per acre",
                          perAcre,
                        };
                      };

                      const preferredSignups = {
                        G: !!practice?.general,
                        C: !!practice?.continuous,
                      };

                      const preferredIncentives = (() => {
                        const updated = { S: 0, R: 0, P: 0, W: 0 };

                        (practice?.programs || []).forEach((p) => {
                          const incentives = p?.incentives;
                          if (!incentives) return;

                          const sip = Number(incentives.sip);
                          if (!isNaN(sip)) updated.S = Math.max(updated.S, sip);

                          const rri = Number(incentives.rentalRateIncentive);
                          if (!isNaN(rri)) updated.R = Math.max(updated.R, rri);

                          const pip = Number(incentives.pip);
                          if (!isNaN(pip)) updated.P = Math.max(updated.P, pip);

                          const wq = Number(incentives.waterqualitythruclear30);
                          if (!isNaN(wq)) updated.W = Math.max(updated.W, wq);
                        });

                        return updated;
                      })();

                      const renderSignupCapSentence = (practiceNumber) => {
                        const { G, C } = preferredSignups || {};

                        if (G && C) {
                          return (
                            <>
                              <b>{practiceNumber}</b> is eligible for both
                              general and continuous signup. General signup has
                              a cap of $240/acre and continuous signup has a cap
                              of $300/acre.
                            </>
                          );
                        }

                        if (G) {
                          return (
                            <>
                              <b>{practiceNumber}</b> is eligible for general
                              signup which has a cap of $240/acre.
                            </>
                          );
                        }

                        if (C) {
                          return (
                            <>
                              <b>{practiceNumber}</b> is eligible for continuous
                              signup which has a cap of $300/acre.
                            </>
                          );
                        }

                        return null;
                      };

                      const renderCrepStateIncentiveSentence = (
                        option,
                        practiceNumber
                      ) => {
                        const stateIncentive = Number(option?.stateincentive);
                        if (
                          !stateIncentive ||
                          isNaN(stateIncentive) ||
                          stateIncentive <= 0
                        )
                          return null;

                        return (
                          <>
                            <b>{practiceNumber}</b> enrolled via the{" "}
                            <b>{option.crepname}</b> initiative may also receive
                            an additional annual{" "}
                            <b>${stateIncentive.toLocaleString()}/acre</b> state
                            incentive.
                          </>
                        );
                      };

                      const { perAcre } = getPerAcreBaseRateInfo(practiceTitle);

                      const basisTitle = isPasturePractice(practiceTitle)
                        ? "Marginal Pastureland Rental Rate"
                        : isGrasslandPractice(practiceTitle)
                        ? "Grassland Rental Rate"
                        : "Predominant Soil Types";

                      const basisValue =
                        perAcre !== null
                          ? `$${perAcre.toLocaleString()}/acre`
                          : "N/A";

                      const basisNote = hasMissingTopThreeSrr
                        ? "Your field includes a soil type which is outside of our data set. Please contact your local USDA office for more information."
                        : (() => {
                            const { G, C } = preferredSignups || {};
                            if (G && C)
                              return "You will receive 85% for general signup and 90% for continuous signup of the estimated base rental rate.";
                            if (G)
                              return "You will receive 85% for general signup of the estimated base rental rate.";
                            if (C)
                              return "You will receive 90% for continuous signup of the estimated base rental rate.";
                            return null;
                          })();

                      const basisTable =
                        !isPasturePractice(practiceTitle) &&
                        !isGrasslandPractice(practiceTitle) ? (
                          <Table
                            className="print-top-soils-table"
                            pagination={false}
                            size="small"
                            columns={[
                              {
                                title: "Type",
                                dataIndex: "soil_type",
                                key: "soil_type",
                                align: "center",
                              },
                              {
                                title: "Rate",
                                dataIndex: "rental_rate",
                                key: "rental_rate",
                                align: "center",
                              },
                              {
                                title: "Acreage",
                                dataIndex: "acreage",
                                key: "acreage",
                                align: "center",
                              },
                            ]}
                            dataSource={topThreeSoilsTable}
                          />
                        ) : null;

                      const rateBasisBlock = (
                        <div className="print-rate-basis">
                          <div className="print-rate-basis-header">
                            <b>{basisTitle}</b>: <span>{basisValue}</span>
                          </div>

                          {basisNote ? (
                            <div className="print-rate-basis-note">
                              <i>{basisNote}</i>
                            </div>
                          ) : null}

                          {basisTable ? (
                            <div className="print-rate-basis-table">
                              {basisTable}
                            </div>
                          ) : null}
                        </div>
                      );

                      const getSignupHeaderText = (preferredSignups) => {
                        const G = !!preferredSignups?.G;
                        const C = !!preferredSignups?.C;

                        if (G && C) return "General and Continuous Signup";
                        if (G) return "General Signup";
                        if (C) return "Continuous Signup";

                        // Fallback if neither flag is set for some reason
                        return "Signup";
                      };

                      const renderIncentiveBlock = ({
                        headerText,
                        borderColor,
                        selectedCrepOption,
                      }) => {
                        const effectiveIncentives = getEffectiveIncentives(
                          preferredIncentives,
                          selectedCrepOption
                        );

                        const incentivesNode =
                          renderIncentives(effectiveIncentives);
                        const hasIncentives = !!incentivesNode;

                        return (
                          <>
                            <div
                              className="print-incentive-block"
                              style={{
                                border: `2px solid ${borderColor || "gray"}`,
                              }}
                            >
                              <div
                                className="print-incentive-block-header"
                                style={{
                                  borderBottom: `1px solid ${
                                    borderColor || "gray"
                                  }`,
                                }}
                              >
                                <b>{headerText}</b>
                              </div>

                              <div
                                className={`print-incentive-block-body ${
                                  hasIncentives
                                    ? "has-incentives"
                                    : "no-incentives"
                                }`}
                              >
                                <div className="print-incentive-title">
                                  Possible Incentives
                                </div>

                                {hasIncentives && (
                                  <div className="print-incentive-block-left">
                                    <div style={{ marginTop: 6 }}>
                                      {incentivesNode}
                                    </div>
                                  </div>
                                )}

                                <div className="print-incentive-block-right">
                                  {!selectedCrepOption ? (
                                    <div>
                                      {renderSignupCapSentence(
                                        practice.practiceNumber
                                      )}
                                    </div>
                                  ) : (
                                    <div>
                                      {renderCrepStateIncentiveSentence(
                                        selectedCrepOption,
                                        practice.practiceNumber
                                      )}
                                    </div>
                                  )}

                                  <div style={{ marginTop: 10 }}>
                                    <i>
                                      *Payment calculations will use the actual
                                      acres enrolled, not the entire field’s
                                      acreage.
                                    </i>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </>
                        );
                      };

                      const baseBlock = renderIncentiveBlock({
                        headerText: getSignupHeaderText(preferredSignups),
                        borderColor: "gray",
                        selectedCrepOption: null,
                      });

                      const crepBlocks = eligibleCREPs.map((crep) =>
                        renderIncentiveBlock({
                          headerText: crep.crepname,
                          borderColor: getCrepColor(crep.crepname) || "#888888",
                          selectedCrepOption: crep,
                        })
                      );

                      return (
                        <List.Item key={practice?.title || index}>
                          <div className="report-practice-detail-container print-practice-block">
                            <PrintHelp
                              disableInteractions={true}
                              practice={practice}
                              listView="Compact"
                              userFavoritePractices={userFavoritePractices}
                              eligibleCREPs={eligibleCREPs}
                              crepColorMap={crepColors}
                              rateBasisTitle={basisTitle}
                              rateBasisValue={basisValue}
                              rateBasisNote={basisNote}
                              rateBasisTable={basisTable}
                            />
                            {rateBasisBlock}
                            {baseBlock}
                            {crepBlocks}
                          </div>
                        </List.Item>
                      );
                    }}
                  />
                }
              </Spin>

              <div className="local-usda-container">
                <h3 className={"report-section-header"}>Local USDA Office:</h3>

                <div className={"report-usda-information"}>
                  <p>
                    Please reach out to your local USDA Farm Service Agency
                    office for further information.
                  </p>

                  <Table
                    className={"report-table"}
                    columns={[
                      {
                        title: "Service Center",
                        dataIndex: "service_center",
                        key: "service_center",
                        align: "center",
                      },
                      {
                        title: "Address",
                        dataIndex: "physical_address",
                        key: "physical_address",
                        align: "center",
                      },
                      {
                        title: "Phone",
                        dataIndex: "phone",
                        key: "phone",
                        align: "center",
                      },
                    ]}
                    dataSource={serviceCenterList}
                    pagination={false}
                    size="large"
                  />
                </div>
              </div>
            </div>
          </div>

          <hr className="report-hr" />

          <div className="report-general-info-container">
            <div>
              <h3 className={"report-section-header"}>Acknowledgement:</h3>
              <div className={"report-disclaimer"}>
                <p>
                  This material is based upon work supported by the U.S.
                  Department of Agriculture, Farm Production and Conservation
                  program under agreement number FBC22CPT0012776. Any opinions,
                  findings, conclusions, or recommendations expressed in this
                  publication are those of the author(s) and do not necessarily
                  reflect the views of the U.S. Department of Agriculture. In
                  addition, any reference to specific brands or types of
                  products or services does not constitute or imply an
                  endorsement by the U.S. Department of Agriculture for those
                  products or services. USDA is an equal opportunity provider,
                  employer, and lender
                  (https://www.usda.gov/non-discrimination-statement). This work
                  is produced with support from the Mississippi State University
                  Forest and Wildlife Research Center and Mississippi State
                  University Geosystems Research Institute.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Spin>
      <Footer />
    </>
  );
};

export default FieldPrint;
